import {getGaClientId} from '@optimaxdev/analytics/desktop/lib/clientId/index';
import {createAction, createAsyncThunk} from '@reduxjs/toolkit';
import {AxiosError, isAxiosError} from 'axios';

import {ENDPOINTS} from 'constants/endpoints';
import {PAYMENTS, PRIMER_METHODS} from 'desktop/pages/checkout/constants/paymentStep';
import {sendPrimerOrderErrorEvents} from 'desktop/pages/checkout/utils/completeOrder/analytics';
import {createPayloadForOrder} from 'desktop/pages/checkout/utils/createPayloadForPayments/createPayloadForPayments';
import {formDataPaypal} from 'desktop/pages/checkout/utils/formDataPaymentMethods/formDataPaymentMethods';

import {StatusType} from 'desktop/types/statusType';
import {getAffId} from 'libs/analytics/affiliate';
import {HTTP_REQUEST_HEADER, HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE, apiConfig} from 'libs/api/api';
import {retryAxiosError} from 'libs/retryError/retryError';
import {flags} from 'libs/rollout';
import {CompleteOrderType, OrderErrorType} from 'types/completeOrderType';
import {PaymentInfoType} from 'types/PaymentMethodsType';

const createPrimerOrder = async (paymentInfo: PaymentInfoType) => {
    try {
        const data = {
            affid: getAffId(),
            payment: {
                external_payment_id: paymentInfo.token,
                method:
                    paymentInfo.method === PAYMENTS.METHOD_PRIMER_FSA
                        ? PAYMENTS.METHOD_PRIMER
                        : paymentInfo.method,
            },
        };
        return await (flags.dPrimerRetryMechanism.isEnabled()
            ? retryAxiosError(attempt =>
                  apiConfig.instanceGusaBackend.request({
                      data,
                      method: 'post',
                      params: {attempt},
                      url: `${ENDPOINTS.CHECKOUT_PLACE_ORDER_V2}`,
                  }),
              )
            : apiConfig.instanceGusaBackend.request({
                  data,
                  method: 'post',
                  url: `${ENDPOINTS.CHECKOUT_PLACE_ORDER_V2}`,
              }));
    } catch (e) {
        if (isAxiosError(e)) {
            const errorData = e.response?.data;
            sendPrimerOrderErrorEvents(
                paymentInfo,
                {
                    error_code: errorData?.error_code || e.code,
                },
                e.response?.status || 'unknown',
            );
        }
        throw e;
    }
};

export const completeOrderReview = createAsyncThunk<
    {data: CompleteOrderType},
    {paymentInfo: PaymentInfoType; formKey: string},
    {
        rejectValue: AxiosError<CompleteOrderType>;
    }
>('CHECKOUT@COMPLETE_ORDER_REVIEW', async ({formKey, paymentInfo}, {rejectWithValue}) => {
    try {
        if (PRIMER_METHODS.includes(String(paymentInfo.method) as PAYMENTS)) {
            return await createPrimerOrder(paymentInfo);
        }
        const paymentMethod =
            paymentInfo.method.toString() === PAYMENTS.METHOD_CREDIT_CARD_FSA
                ? PAYMENTS.METHOD_CREDIT_CARD
                : paymentInfo.method.toString();

        const data = createPayloadForOrder(paymentMethod, {formKey, paymentInfo});
        data.append('ga_cid', getGaClientId());

        const response = await apiConfig.instanceGusaBackend.request({
            data,
            headers: {
                [HTTP_REQUEST_HEADER.CONTENT_TYPE]:
                    HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE.MULTIPART_FORM_DATA,
            },
            method: 'post',
            url: `${ENDPOINTS.CHECKOUT_PLACE_ORDER}`,
        });
        return response;
    } catch (e) {
        return rejectWithValue(e);
    }
});

export const completeOrderReviewPending = createAction('CHECKOUT@COMPLETE_ORDER_REVIEW_PENDING');
export const completeOrderReviewResetPending = createAction(
    'CHECKOUT@COMPLETE_ORDER_REVIEW_RESET_PENDING',
);

export const completeOrderReviewPaypal = createAsyncThunk<
    {data: CompleteOrderType},
    PaymentInfoType,
    {
        rejectValue: {response: {data: CompleteOrderType}};
    }
>('CHECKOUT@COMPLETE_ORDER_REVIEW_PAYPAL', async (paymentInfo, {rejectWithValue}) => {
    const data = formDataPaypal(paymentInfo);
    data.append('ga_cid', getGaClientId());

    try {
        const response = await apiConfig.instanceGusaBackend.request({
            data,
            headers: {
                [HTTP_REQUEST_HEADER.CONTENT_TYPE]:
                    HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE.MULTIPART_FORM_DATA,
            },
            method: 'post',
            url: `${ENDPOINTS.CHECKOUT_PLACE_ORDER_PAYPAL}`,
        });
        return response;
    } catch (e) {
        return rejectWithValue(e);
    }
});

export const setDefaultStateForCompleteOrder = createAction<StatusType>(
    'CHECKOUT@SET_DEFAULT_STATE_FOR_COMPLETE_ORDER',
);

export const placeOrder = createAction('CHECKOUT@PLACE_ORDER');
export const setPlaceOrderErrors = createAction<Array<OrderErrorType>>(
    'CHECKOUT@SET_PLACE_ORDER_ERRORS',
);
